import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './EventComments.css';
import { translateError } from '../../utils/errorMessages';

interface CommentAuthor {
  _id: string;
  name: string;
}

interface Reply {
  _id: string;
  content: string;
  author: CommentAuthor;
  createdAt: string;
}

interface Comment {
  _id: string;
  content: string;
  author: CommentAuthor;
  createdAt: string;
  replies: Reply[];
}

interface EventCommentsProps {
  eventId: string;
  // Optional override: force the member (/api/my) or admin (/api/events) path.
  // Defaults to !isAdmin so members hit the policy-gated /my routes (admins are
  // 403'd off /my, members are 403'd off the admin routes).
  useMyApi?: boolean;
}

const EventComments: React.FC<EventCommentsProps> = ({ eventId, useMyApi }) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const viaMy = useMyApi ?? !isAdmin;
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { markCommentsRead } = useNotifications();

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = viaMy
        ? await api.my.eventComments.list(eventId)
        : await apiClient.get<Comment[]>(`/api/events/${eventId}/comments`);

      setComments(data as Comment[]);
      setLoading(false);

      // Mark comments as read
      markCommentsRead(eventId);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, [eventId, viaMy, markCommentsRead]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newComment.trim() || submitting) return;

    setSubmitting(true);

    try {
      const { data } = viaMy
        ? await api.my.eventComments.create(eventId, { content: newComment.trim() })
        : await apiClient.post<Comment>(`/api/events/${eventId}/comments`, { content: newComment.trim() });

      // Add new comment to list
      const commentToAdd: Comment = {
        _id: data._id,
        content: data.content,
        author: data.author,
        createdAt: data.createdAt,
        replies: [],
      };
      setComments(prev => [...prev, commentToAdd]);
      setNewComment('');
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentCommentId: string) => {
    if (!replyContent.trim() || submitting) return;

    setSubmitting(true);

    try {
      const { data } = viaMy
        ? await api.my.eventComments.create(eventId, { content: replyContent.trim(), parentCommentId })
        : await apiClient.post<Reply>(`/api/events/${eventId}/comments`, { content: replyContent.trim(), parentCommentId });

      // Add reply to parent comment
      const replyToAdd: Reply = {
        _id: data._id,
        content: data.content,
        author: data.author,
        createdAt: data.createdAt,
      };
      setComments(prev =>
        prev.map(comment =>
          comment._id === parentCommentId
            ? { ...comment, replies: [...comment.replies, replyToAdd] }
            : comment
        )
      );
      setReplyContent('');
      setReplyingTo(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('comments.justNow');
    if (minutes < 60) return t('comments.minutesAgo', { count: minutes });
    if (hours < 24) return t('comments.hoursAgo', { count: hours });
    if (days < 7) return t('comments.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="event-comments__loading">
        <div className="event-comments__spinner"></div>
      </div>
    );
  }

  return (
    <div className="event-comments">
      <h4 className="event-comments__title">{t('comments.title')}</h4>

      {error && (
        <div className="event-comments__error">{error}</div>
      )}

      {/* Comment input */}
      <form className="event-comments__form" onSubmit={handleSubmitComment}>
        <div className="event-comments__input-wrapper">
          <textarea
            value={newComment}
            onChange={(e) => {
              setNewComment(e.target.value);
              // Auto-expand
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (newComment.trim() && !submitting) {
                  handleSubmitComment(e);
                }
              }
            }}
            placeholder={t('comments.placeholder')}
            disabled={submitting}
            rows={1}
          />
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="event-comments__send-btn"
            aria-label={t('comments.post')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"></line>
              <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="event-comments__list">
        {comments.length === 0 ? (
          <p className="event-comments__empty">{t('comments.noComments')}</p>
        ) : (
          comments.map(comment => (
            <div key={comment._id} className="event-comments__comment">
              <div className="event-comments__avatar">
                {comment.author.name.charAt(0).toUpperCase()}
              </div>
              <div className="event-comments__content">
                <div className="event-comments__header">
                  <span className="event-comments__author">{comment.author.name}</span>
                  <span className="event-comments__time">{formatTime(comment.createdAt)}</span>
                </div>
                <p className="event-comments__text">{comment.content}</p>
                <button
                  className="event-comments__reply-btn"
                  onClick={() => setReplyingTo(replyingTo === comment._id ? null : comment._id)}
                >
                  {t('comments.reply')}
                </button>

                {/* Replies */}
                {comment.replies.length > 0 && (
                  <div className="event-comments__replies">
                    {comment.replies.map(reply => (
                      <div key={reply._id} className="event-comments__reply">
                        <div className="event-comments__avatar event-comments__avatar--small">
                          {reply.author.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="event-comments__content">
                          <div className="event-comments__header">
                            <span className="event-comments__author">{reply.author.name}</span>
                            <span className="event-comments__time">{formatTime(reply.createdAt)}</span>
                          </div>
                          <p className="event-comments__text">{reply.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply form */}
                {replyingTo === comment._id && (
                  <div className="event-comments__reply-form">
                    <div className="event-comments__input-wrapper">
                      <textarea
                        value={replyContent}
                        onChange={(e) => {
                          setReplyContent(e.target.value);
                          // Auto-expand
                          e.target.style.height = 'auto';
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (replyContent.trim() && !submitting) {
                              handleSubmitReply(comment._id);
                            }
                          }
                        }}
                        placeholder={t('comments.replyPlaceholder')}
                        disabled={submitting}
                        autoFocus
                        rows={1}
                      />
                      <button
                        onClick={() => handleSubmitReply(comment._id)}
                        disabled={submitting || !replyContent.trim()}
                        className="event-comments__send-btn"
                        aria-label={t('comments.post')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EventComments;
